import { useCallback } from "react";

import type { Awaitable } from "@alaffia-technology-solutions/types";

import { useEventCallback } from "@alaffia-technology-solutions/hooks";

import { useShallow } from "zustand/react/shallow";

import type { PDFControllerSlice } from "./providers/state";

import { usePDFStore } from "./providers";

export function usePDF() {
  const selector = useShallow((s: PDFControllerSlice) => ({
    addEventListener: s.addEventListener,
    addHighlight: s.addHighlight,
    bookmarks: s.bookmarks,
    close: s.close,
    currentPageIndex: s.currentPageIndex,
    error: s.error,
    events: s.events,
    getPageDimensions: s.getPageDimensions,
    getPageThumbnail: s.getPageThumbnail,
    getTextRect: s.getTextRect,
    highlightTextOnPage: s.highlightTextOnPage,
    jumpToPage: s.jumpToPage,
    loadBookmarks: s.loadBookmarks,
    nextPage: s.nextPage,
    open: s.open,
    state: s.state,
    pageCount: s.pageCount,
    pdfUrl: s.pdfUrl,
    prevPage: s.prevPage,
    readOnlyScale: s.scale,
    removeEventListener: s.removeEventListener,
    removeHighlights: s.removeHighlights,
    removeHighlightsOnPage: s.removeHighlightsOnPage,
    rotate: s.rotate,
    setViewMode: s.setViewMode,
    viewMode: s.viewMode,
    zoom: s.zoom,
  }));

  const { data: controller } = usePDFStore(selector);

  const close = useEventCallback(controller?.close);
  const getPageDimensions = useControllerCallback(
    controller?.getPageDimensions,
  );
  const getPageThumbnail = useControllerCallback(controller?.getPageThumbnail);

  const getTextRect = useControllerCallback(controller?.getTextRect);

  const highlightTextOnPage = useControllerCallback(
    controller?.highlightTextOnPage,
  );

  const jumpToPage = useControllerCallback(controller?.jumpToPage);
  const loadBookmarks = useControllerCallback(controller?.loadBookmarks);
  const nextPage = useControllerCallback(controller?.nextPage);
  const open = useControllerCallback(controller?.open);
  const prevPage = useControllerCallback(controller?.prevPage);

  const removeHighlights = useControllerCallback(controller?.removeHighlights);
  const removeHighlightsOnPage = useControllerCallback(
    controller?.removeHighlightsOnPage,
  );

  const rotate = useControllerCallback(controller?.rotate);
  const setViewMode = useEventCallback(controller?.setViewMode);
  const zoom = useControllerCallback(controller?.zoom);
  const addHighlight = useControllerCallback(controller?.addHighlight);

  return {
    addEventListener: controller?.addEventListener,
    addHighlight,
    bookmarks: controller?.bookmarks ?? null,
    close,
    currentPage: controller?.currentPageIndex ?? 0,
    error: controller?.error,
    events: controller?.events,
    getPageDimensions,
    getPageThumbnail,
    getTextRect,
    highlightTextOnPage,
    jumpToPage,
    loadBookmarks,
    nextPage,
    open,
    state: controller?.state ?? "closed",
    pagesCount: controller?.pageCount ?? 0,
    prevPage,
    readOnlyScale: controller?.readOnlyScale ?? "fitWidth",
    removeEventListener: controller?.removeEventListener,
    removeHighlights,
    removeHighlightsOnPage,
    rotate,
    setViewMode,
    viewMode: controller?.viewMode ?? null,
    zoom,
  };
}

function useControllerCallback<TArgs extends unknown[], TReturn>(
  callback: ((...args: TArgs) => Awaitable<TReturn>) | undefined,
) {
  return useCallback(
    async (...args: TArgs) => {
      const result = await callback?.(...args);
      return result ?? null;
    },
    [callback],
  );
}
